/**
 * Hook for the optional, on-demand AI flow review.
 *
 * Calls `POST /workflows/:id/review`, which streams Server-Sent Events: live
 * progress/activity while the agent runs, then a terminal `complete` (with the
 * scorecard + comments) or `error`. The server may also create a durable
 * `agent`-sourced proposal (result kind: 'proposed'), in which case we
 * invalidate the proposals list so it appears in the pill/tray.
 *
 * See `apps/api/src/workflows/routes/workflow-snapshots.routes.ts` and
 * `apps/api/src/workflows/review/stream.ts`.
 */

import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type {
  WorkflowReviewProgressEvent,
  WorkflowReviewResult,
  WorkflowReviewStreamEvent,
} from '@chatsdet/types';
import { API_URL } from '../../../../config';
import { toApiError } from '../../../../lib/api-error';

export type RequestReviewHandlers = {
  /** Called for each live progress/activity/text event while the agent runs. */
  onEvent: (event: WorkflowReviewProgressEvent) => void;
};

/** Parse a raw SSE chunk buffer into complete `data:` payloads. Returns the
 *  parsed events and the unconsumed tail to carry into the next read. */
function drainSseFrames(buffer: string): {
  events: WorkflowReviewStreamEvent[];
  rest: string;
} {
  const events: WorkflowReviewStreamEvent[] = [];
  const frames = buffer.split('\n\n');
  const rest = frames.pop() ?? '';
  for (const frame of frames) {
    const dataLine = frame
      .split('\n')
      .find((l) => l.startsWith('data:'));
    if (!dataLine) continue;
    try {
      events.push(JSON.parse(dataLine.slice(5).trim()) as WorkflowReviewStreamEvent);
    } catch {
      // Ignore malformed frames (e.g. keepalive comments).
    }
  }
  return { events, rest };
}

export function useReviewWorkflow(workflowId: string | null) {
  const queryClient = useQueryClient();

  const requestReview = React.useCallback(
    async ({ onEvent }: RequestReviewHandlers): Promise<WorkflowReviewResult> => {
      if (!workflowId) throw new Error('Missing workflow ID');

      const res = await fetch(`${API_URL}/workflows/${workflowId}/review`, {
        method: 'POST',
        credentials: 'include',
        headers: { accept: 'text/event-stream' },
      });
      if (!res.ok || !res.body) throw await toApiError(res, 'Failed to review flow');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let result: WorkflowReviewResult | null = null;

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (value) buffer += decoder.decode(value, { stream: true });
        const { events, rest } = drainSseFrames(buffer);
        buffer = rest;
        for (const event of events) {
          if (event.type === 'complete') {
            result = event.result;
          } else if (event.type === 'error') {
            throw new Error(event.message);
          } else {
            onEvent(event);
          }
        }
        if (done) break;
      }

      if (!result) throw new Error('Review ended without a result');

      if (result.kind === 'proposed') {
        queryClient.invalidateQueries({ queryKey: ['workflow-proposals'] });
      }
      return result;
    },
    [workflowId, queryClient],
  );

  return { requestReview };
}
